import { AlgoliaObject } from '@marmon/shared/types/algolia';
import { Analysis, AnalysisStatus } from '../interfaces';
import { getAnalysisStatus } from '../utils/algolia-analysis.utils';

export class AlgoliaAnalysis extends AlgoliaObject {
	name!: string;
	domain!: string;
	expiresAt!: number;
	createdById!: string;
	status!: AnalysisStatus;

	override _tags: string[] = [];

	constructor(base: Analysis) {
		super(base);

		this.name = base.name;
		this.domain = base.domain;
		this.expiresAt = base.expiresAt.toMillis();
		this.createdById = base.createdBy.id;
		this.status = getAnalysisStatus(base);

		this._tags = [
			`domain:${this.domain}`,
			`owner:${this.createdById}`,
			`status:${this.status}`,
			...base.shared.map((ref) => accessTag(ref.id)),
		].sort(this.sort);
	}

	/**
	 * Determines if this algolia analysis instance is equal to another one. We avoid
	 * checking against the created and updated timestamps due to those values not holding
	 * any merit or value in terms of the Algolia data object. We want to ensure that
	 * we are only updating the algolia instance as little as possible.
	 *
	 * @param other the other algolia analysis to compare against
	 */
	isEqual(other: AlgoliaAnalysis) {
		if (other.objectID !== this.objectID) return false;
		if (other.name !== this.name) return false;
		if (other.domain !== this.domain) return false;
		if (other.expiresAt !== this.expiresAt) return false;
		if (other.createdById !== this.createdById) return false;
		if (other.status !== this.status) return false;
		if (other._tags.length !== this._tags.length) return false;

		// sort tags to ensure they are always in the right order
		const otherTags = JSON.stringify(other._tags.sort(this.sort));
		const tags = JSON.stringify(this._tags.sort(this.sort));
		if (otherTags !== tags) return false;

		return true;
	}

	/**
	 * Reusable utility to sort strings in the class. Mainly used for Algolia Tags
	 * to ensure they are in ASC alphabetical order.
	 */
	private readonly sort = (a: string, b: string) =>
		a > b ? 1 : a < b ? -1 : 0;
}

export const accessTag = (id: string) => `access:${id}`;
